/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useAuthenticationContext, useAuthQuery } from '@opengeoweb/api';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { getTAC, getTafList, patchTaf, postTaf, getTafConfig } from './api';
import type { TafError } from '../../components/TafLayout/utils';
import {
  getSnackbarMessage,
  isWarning,
  parseErrorMessage,
} from '../../components/TafLayout/utils';
import type {
  TAC,
  Taf,
  TafFromBackend,
  TafFromFrontEnd,
  TafConfig,
  TafType,
} from '../../types';
import { useTafTranslation } from '../i18n';

export const useTafList = (
  type: TafType = 'TAF',
  disabled = false,
): UseQueryResult<TafFromBackend[]> =>
  useAuthQuery(
    ['taf-list', type],
    async (auth) => getTafList(auth, type),
    disabled,
    1000 * 60 * 5,
  );

export const useTafConfig = (
  type: TafType = 'TAF',
  disabled = false,
): UseQueryResult<TafConfig> =>
  useAuthQuery(
    ['taf-config', type],
    async (auth) => getTafConfig(auth, type),
    disabled,
    false,
  );

type PostReturnType<T> = UseMutationResult<Response | null, Error, T>;
interface PostTafProps {
  tafData: TafFromFrontEnd;
  isAutoSaved: boolean;
  setError: React.Dispatch<React.SetStateAction<TafError | null>>;
}
export const usePostTaf = (): PostReturnType<PostTafProps> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();
  const { t } = useTafTranslation();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ tafData, setError }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      setError(null);
      return postTaf(auth, tafData);
    },
    onSuccess: async (_data, { tafData, isAutoSaved }) => {
      const message = getSnackbarMessage(tafData, isAutoSaved, t);
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message,
        }),
      );
      // trigger refetch for the list
      await queryClient.invalidateQueries({
        queryKey: ['taf-list'],
      });
    },
    onError: async (error, { tafData, setError }) => {
      const tafError = parseErrorMessage(error, tafData, t);
      setError(tafError);
      if (isWarning(tafError)) {
        // trigger refetch for the list
        await queryClient.invalidateQueries({
          queryKey: ['taf-list'],
        });
      }
    },
  });
};

type PatchReturnType<T> = UseMutationResult<Response | null, Error, T>;
interface PatchTafProps {
  tafData: TafFromFrontEnd;
  setError: React.Dispatch<React.SetStateAction<TafError | null>>;
  force?: boolean;
}
export const usePatchTaf = (): PatchReturnType<PatchTafProps> => {
  const queryClient = useQueryClient();
  const { auth } = useAuthenticationContext();
  const { t } = useTafTranslation();

  return useMutation({
    mutationFn: async ({ tafData, setError, force }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      setError(null);
      return patchTaf(auth, tafData, force);
    },
    onSuccess: async () => {
      // trigger refetch for the list
      await queryClient.invalidateQueries({ queryKey: ['taf-list'] });
    },
    onError: async (error, { tafData, setError }) => {
      if (error.cause === 409) {
        // trigger refetch for the list
        await queryClient.invalidateQueries({ queryKey: ['taf-list'] });
      } else {
        // no refetch, show error message
        const newError = parseErrorMessage(error as Error, tafData, t);
        setError(newError);
      }
    },
  });
};

type GetTACReturnType<T> = UseMutationResult<TAC, Error, T>;
interface GetTACProps {
  tafData: Taf;
}
export const useGetTAC = (): GetTACReturnType<GetTACProps> => {
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ tafData }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return getTAC(auth, tafData);
    },
  });
};
